/*
  Product video autoplay (Stickiply, 12 Sep 2026).
  Product media of type video renders as <autoplay-media> (see snippets/product-thumbnail.liquid).
  It extends Dawn's DeferredMedia: the video loads and plays, muted, once half of it is on screen,
  and pauses when it scrolls away. Where autoplay is not allowed (see autoplayAllowed) the
  poster and play button stay and the customer taps to play, exactly as Dawn does.
*/
if (!customElements.get('autoplay-media')) {
  customElements.define(
    'autoplay-media',
    class AutoplayMedia extends DeferredMedia {
      // Autoplay only where it will not cost the customer: not with reduced motion or Save-Data,
      // and not on phones whose browser cannot stream HLS (they would fetch the full-size MP4).
      static autoplayAllowed() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
        if (navigator.connection && navigator.connection.saveData) return false;
        const probe = document.createElement('video');
        const hls = probe.canPlayType('application/vnd.apple.mpegurl') || probe.canPlayType('application/x-mpegURL');
        if (window.innerWidth < 750 && !hls) return false;
        return true;
      }

      connectedCallback() {
        if (this.observer) return;
        this.bindSoundButton();
        if (!AutoplayMedia.autoplayAllowed()) return;
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => (entry.isIntersecting ? this.playVideo() : this.pauseVideo())),
          { threshold: 0.5 }
        );
        this.observer.observe(this);
        this.markInteraction = () => (this.lastInteraction = Date.now());
        this.addEventListener('pointerdown', this.markInteraction, true);
        this.addEventListener('keydown', this.markInteraction, true);
        this.onVisibility = () => {
          if (!document.hidden && this.visible) this.playVideo();
        };
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      bindSoundButton() {
        const button = this.querySelector('.product__video-sound');
        if (!button || button.dataset.bound) return;
        button.dataset.bound = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const video = this.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          if (!video.muted && video.paused) video.play().catch(() => {});
          this.syncSoundButton();
        });
      }

      syncSoundButton() {
        const button = this.querySelector('.product__video-sound');
        const video = this.querySelector('video');
        if (!button || !video) return;
        const on = !video.muted;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
        button.setAttribute('aria-label', on ? button.dataset.labelOn : button.dataset.labelOff);
        button.querySelector('.product__video-sound-icon--on').hidden = !on;
        button.querySelector('.product__video-sound-icon--off').hidden = on;
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      // Poster tap (the path where autoplay is not allowed, and Dawn's thumbnail click): the video has
      // no autoplay attribute and no controls, so start it here after Dawn has inserted it.
      loadContent(focus = false) {
        const first = !this.getAttribute('loaded');
        super.loadContent(focus);
        if (!first) return;
        const video = this.querySelector('video');
        if (!video) return;
        this.visible = true;
        video.load();
        window.setTimeout(() => video.play().catch(() => {}), 0);
      }

      playVideo() {
        this.visible = true;
        const justLoaded = !this.getAttribute('loaded');
        if (justLoaded) this.loadContent(false);
        const video = this.querySelector('video');
        if (!video) return;
        const attempt = () => {
          if (this.visible && video.paused && !video.ended) video.play().catch(() => {});
        };
        if (!video.dataset.autoplayBound) {
          video.dataset.autoplayBound = 'true';
          video.addEventListener('volumechange', () => this.syncSoundButton());
          video.addEventListener('loadedmetadata', attempt);
          video.addEventListener('canplay', attempt);
          video.addEventListener('pause', () => {
            // Dawn's pauseAllMedia() stops every video when a modal opens or closes or a slide changes.
            // Resume unless the video left the screen, ended, or the customer paused it themselves.
            if (!this.visible || video.ended) return;
            if (Date.now() - (this.lastInteraction || 0) < 600) return;
            window.setTimeout(attempt, 60);
          });
        }
        // Safari: a <video> cloned out of a <template> does not start resource selection on its own,
        // so play() in the same task as the insertion never settles. load() first, play on the next task,
        // and retry once if nothing has loaded (readyState 0) after 1.5 s.
        if (justLoaded) video.load();
        window.setTimeout(attempt, 0);
        window.setTimeout(() => {
          if (this.visible && video.readyState === 0) {
            video.load();
            video.play().catch(() => {});
          }
        }, 1500);
      }

      pauseVideo() {
        this.visible = false;
        const video = this.querySelector('video');
        if (video && !video.paused) video.pause();
      }
    }
  );
}
